import { useCallback } from "react";
import { useUIStore } from "@/stores/useUIStore";
import { resolveContent } from "@/lib/questContent/resolveContent";
import type { ContentI18n, LocalizedContent } from "@/lib/questContent/types";

/**
 * DEV-824 follow-up: a localizer for surfaces that render content in a LOOP.
 *
 * `useQuestContent` resolves exactly one card and is the right tool inside a
 * card component. The /work lists map over rows in a single component, where
 * one hook call per row would break the rules of hooks. This reads the
 * language once and hands back a pure function to apply per row.
 *
 * The returned callback is stable per language, so a list re-renders only when
 * the reader actually changes language.
 */
export function useContentLocalizer(): (
  source: { title: string; description?: string },
  i18n: ContentI18n | null | undefined,
  sourceLang: string | null | undefined,
) => LocalizedContent {
  const locale = useUIStore((s) => s.language);

  return useCallback(
    (source, i18n, sourceLang) =>
      resolveContent({
        source: { title: source.title, description: source.description ?? "" },
        i18n,
        sourceLang: sourceLang ?? null,
        locale,
      }),
    [locale],
  );
}
